"use client";

import { Link } from "@/components/LocaleLink";
import { useTranslation } from "@/lib/hooks/useTranslation";

// Discord icon
function DiscordIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028 14.09 14.09 0 0 0 1.226-1.994.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
    </svg>
  );
}

// X (Twitter) icon
function XIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
    </svg>
  );
}

export function Footer() {
  const { locale } = useTranslation();
  const currentYear = new Date().getFullYear();
  const isFr = locale === "fr";

  const columns = [
    {
      title: isFr ? "Outils" : "Tools",
      links: [
        { label: isFr ? "Analyse CV ATS" : "ATS resume analysis", href: "/analyse-cv" },
        { label: isFr ? "Simulateur d'entretien" : "Interview simulator", href: "/register" },
        { label: isFr ? "Tarifs" : "Pricing", href: "/pricing" },
        { label: isFr ? "Extension Chrome" : "Chrome extension", href: "/extension" },
      ],
    },
    {
      title: isFr ? "Entraînement" : "Practice",
      links: [
        { label: isFr ? "Entretien tech" : "Tech interview", href: "/entretien-tech" },
        { label: isFr ? "Entretien finance" : "Finance interview", href: "/entretien-finance" },
        { label: isFr ? "Entretien consulting" : "Consulting interview", href: "/entretien-consulting" },
      ],
    },
    {
      title: isFr ? "Ressources" : "Resources",
      links: [
        { label: isFr ? "Tous les guides" : "All guides", href: "/guides" },
        { label: "Blog", href: "/blog" },
        { label: isFr ? "Passer un ATS en 2026" : "Passing an ATS in 2026", href: "/blog/comment-passer-ats-cv-2026" },
        { label: isFr ? "Erreurs CV ATS" : "ATS resume mistakes", href: "/blog/erreurs-cv-ats-score-faible" },
        { label: isFr ? "CV ATS par métier" : "ATS resume by industry", href: "/blog/cv-ats-tech-finance-consulting" },
        { label: "FAQ", href: "/faq" },
      ],
    },
  ];

  return (
    <footer className="border-t bg-muted/30">
      <div className="container mx-auto px-4 pt-12 pb-6">
        {/* Top — 3-column sitemap */}
        <div className="grid grid-cols-2 gap-8 md:grid-cols-3 mb-10">
          {columns.map((col) => (
            <div key={col.title}>
              <h3 className="text-sm font-semibold mb-4 text-foreground">{col.title}</h3>
              <ul className="space-y-2.5">
                {col.links.map((link) => (
                  <li key={link.href}>
                    <Link
                      href={link.href}
                      className="text-sm text-muted-foreground hover:text-primary transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom — legal + social */}
        <div className="flex flex-col items-center justify-between gap-4 pt-6 border-t md:flex-row">
          <div className="flex items-center gap-4">
            <p className="text-sm text-muted-foreground">
              © {currentYear} Velyq
            </p>
            <div className="flex items-center gap-2">
              <a
                href="https://discord.gg/hbevuxEKCq"
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-colors"
                aria-label="Discord"
              >
                <DiscordIcon className="h-4 w-4" />
              </a>
              <a
                href="https://x.com/velyq_ai"
                target="_blank"
                rel="noopener noreferrer"
                className="flex h-8 w-8 items-center justify-center rounded-full bg-muted text-muted-foreground hover:bg-primary hover:text-primary-foreground transition-colors"
                aria-label="X (Twitter)"
              >
                <XIcon className="h-4 w-4" />
              </a>
            </div>
          </div>

          <nav className="flex flex-wrap items-center gap-4 text-sm">
            <Link
              href="/legal"
              className="text-muted-foreground hover:text-primary transition-colors"
            >
              {isFr ? "Mentions légales" : "Legal Notice"}
            </Link>
            <Link
              href="/terms"
              className="text-muted-foreground hover:text-primary transition-colors"
            >
              {isFr ? "CGU" : "Terms"}
            </Link>
            <Link
              href="/privacy"
              className="text-muted-foreground hover:text-primary transition-colors"
            >
              {isFr ? "Confidentialité" : "Privacy"}
            </Link>
            <a
              href="mailto:contact@velyq.com"
              className="text-muted-foreground hover:text-primary transition-colors"
            >
              Contact
            </a>
          </nav>
        </div>
      </div>
    </footer>
  );
}
